import { useEffect, useState } from 'react';
import { router } from '%app/js/common/services/router';
import logger from '%app/js/common/services/Logger';
import useDebouncedValue from '../../services/useDebouncedValue';

const AUTOCOMPLETE_ROUTE = 'evp_ticket_frontend.event_search.autocomplete.event_by_term';
const MIN_QUERY_LENGTH = 4;
const REQUEST_DEBOUNCE_MS = 500;

const EMPTY_STATE = Object.freeze({
    suggestions: [], hasMore: false, loading: false, error: null, fetchedQuery: null,
});

function normalizeHasMore(payload) {
    return Boolean(payload && payload.pagination && payload.pagination.more === true);
}

function normalizeSuggestions(payload) {
    if (!payload || !Array.isArray(payload.results)) {
        return [];
    }
    return payload.results
        .filter(entry => entry && (typeof entry.id === 'number' || typeof entry.id === 'string'))
        .map(entry => ({
            id: entry.id,
            text: typeof entry.text === 'string' ? entry.text : '',
            slug: typeof entry.slug === 'string' ? entry.slug : '',
            dateStarts: typeof entry.date_starts === 'string' ? entry.date_starts : '',
            location: typeof entry.location === 'string' ? entry.location : '',
        }));
}

export default function useEventNameSuggestions(query) {
    const trimmed = (query || '').trim();
    const debounced = useDebouncedValue(trimmed, REQUEST_DEBOUNCE_MS);
    const [state, setState] = useState(EMPTY_STATE);

    useEffect(() => {
        if (debounced.length < MIN_QUERY_LENGTH) {
            setState({ ...EMPTY_STATE, fetchedQuery: null });
            return undefined;
        }

        const controller = new AbortController();
        let aborted = false;

        setState(prev => ({ ...prev, loading: true, error: null }));

        async function fetchSuggestions() {
            try {
                const url = `${router.generate(AUTOCOMPLETE_ROUTE)}?q=${encodeURIComponent(debounced)}`;
                const response = await fetch(url, {
                    credentials: 'same-origin',
                    headers: { Accept: 'application/json' },
                    signal: controller.signal,
                });
                if (!response.ok) {
                    throw new Error(`Event autocomplete request failed: ${response.status}`);
                }
                const payload = await response.json();
                if (aborted) {
                    return;
                }
                setState({
                    suggestions: normalizeSuggestions(payload),
                    hasMore: normalizeHasMore(payload),
                    loading: false,
                    error: null,
                    fetchedQuery: debounced,
                });
            } catch (error) {
                if (aborted || error.name === 'AbortError') {
                    return;
                }
                logger.warning('useEventNameSuggestions: failed to load suggestions', { error });
                setState({
                    suggestions: [], hasMore: false, loading: false, error, fetchedQuery: debounced,
                });
            }
        }

        fetchSuggestions();

        return () => {
            aborted = true;
            controller.abort();
        };
    }, [debounced]);

    const belowMinimum = trimmed.length < MIN_QUERY_LENGTH;
    const awaitingFetch = !belowMinimum && state.fetchedQuery !== trimmed;

    return {
        suggestions: belowMinimum ? [] : state.suggestions,
        hasMore: !belowMinimum && state.hasMore,
        loading: !belowMinimum && (state.loading || awaitingFetch),
    };
}

export { MIN_QUERY_LENGTH, REQUEST_DEBOUNCE_MS };
